import { useMemo, useEffect } from 'react';
import { useSeoAuditReportPolling } from '../useSeoAuditReportPolling';
import { transformContentSection, transformIndexingSection, transformMobileSection, transformStructuredDataSection, transformSecuritySection, transformPerformanceSection, transformTechnologiesSection, transformBrandingSection, transformDomainSection, transformBacklinksSection, transformTrafficSection, transformSocialProfilesSection, transformLocalSection } from '../../shared/transformers';

/**
 * Hook for managing SEO report data fetching and transformation
 * @param {string} token - The SEO audit report token
 * @returns {Object} Report data states and transformed section data
 */
export const useSeoReportData = (token) => {
  // Use polling hook to fetch SEO audit data
  const {
    isPolling,
    isReady,
    isError,
    isTimeout,
    seoAuditData,
    errorMessage,
    retry,
  } = useSeoAuditReportPolling(token);

  // Transform SEO audit data for content section
  const contentSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformContentSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for indexing section
  const indexingSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformIndexingSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for mobile section
  const mobileSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformMobileSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for structured data section
  const structuredDataSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformStructuredDataSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for security section
  const securitySectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformSecuritySection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for performance section (minification only for legacy parity)
  const performanceSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformPerformanceSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for technologies section
  const technologiesSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformTechnologiesSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for branding section
  const brandingSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformBrandingSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for domain section
  const domainSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformDomainSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for backlinks section (score only for legacy parity)
  const backlinksSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformBacklinksSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for traffic section
  const trafficSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformTrafficSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for social profiles section
  const socialProfilesSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformSocialProfilesSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Transform SEO audit data for local section
  const localSectionData = useMemo(() => {
    if (!isReady || !seoAuditData) {
      return null;
    }
    return transformLocalSection(seoAuditData);
  }, [isReady, seoAuditData]);

  // Log SEO audit data to console when ready (for debugging)
  useEffect(() => {
    if (isReady && seoAuditData) {
      console.log('SEO Audit Data received:', seoAuditData);
      console.log('Content Section Data:', contentSectionData);
      console.log('Indexing Section Data:', indexingSectionData);
      console.log('Mobile Section Data:', mobileSectionData);
      console.log('Structured Data Section Data:', structuredDataSectionData);
      console.log('Security Section Data:', securitySectionData);
      console.log('Performance Section Data:', performanceSectionData);
      console.log('Technologies Section Data:', technologiesSectionData);
      console.log('Branding Section Data:', brandingSectionData);
      console.log('Domain Section Data:', domainSectionData);
      console.log('Backlinks Section Data:', backlinksSectionData);
      console.log('Traffic Section Data:', trafficSectionData);
      console.log('Social Profiles Section Data:', socialProfilesSectionData);
      console.log('Local Section Data:', localSectionData);
    }
  }, [isReady, seoAuditData, contentSectionData, indexingSectionData, mobileSectionData, structuredDataSectionData, securitySectionData, performanceSectionData, technologiesSectionData, brandingSectionData, domainSectionData, backlinksSectionData, trafficSectionData, socialProfilesSectionData, localSectionData]);

  // Extract URL from API response
  const reportUrl = useMemo(() => {
    return seoAuditData?.data?.url || '';
  }, [seoAuditData]);

  return {
    // Polling states
    isPolling,
    isReady,
    isError,
    isTimeout,
    errorMessage,
    retry,

    // Raw data
    seoAuditData,
    reportUrl,

    // Transformed section data
    contentSectionData,
    indexingSectionData,
    mobileSectionData,
    structuredDataSectionData,
    securitySectionData,
    performanceSectionData,
    technologiesSectionData,
    brandingSectionData,
    domainSectionData,
    backlinksSectionData,
    trafficSectionData,
    socialProfilesSectionData,
    localSectionData,
  };
};

export default useSeoReportData;

